/**
 * Wizard State Persistence Utilities
 *
 * Handles sessionStorage persistence for signup wizard state.
 * State persists for 30 minutes to allow for OAuth redirects and
 * brief interruptions while maintaining security.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

/**
 * Wizard state stored in sessionStorage
 * All sensitive data (password, confirmPassword) is excluded from persistence
 */
export interface WizardState {
  currentStep: 1 | 2 | 3;

  // Step 1 data
  termsReviewed: boolean;
  privacyReviewed: boolean;
  termsAccepted: boolean;
  gdprConsent: boolean;
  marketingConsent: boolean;
  consentTimestamp: string | null;

  // Step 2 data
  selectedMethod: 'email' | 'google' | 'microsoft' | 'apple' | null;

  // Step 3 data (non-sensitive only)
  name: string;
  email: string;
  // Note: password and confirmPassword are NOT persisted for security

  // Meta
  startedAt: string;
  lastActiveAt: string;
}

/**
 * OAuth consent data stored before OAuth redirect
 * This data survives the OAuth redirect and is passed to the backend
 */
export interface OAuthConsentData {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  gdprConsent: boolean;
  marketingConsent: boolean;
  consentTimestamp: string;
}

/**
 * OAuth consent data with storage metadata for TTL validation
 */
interface StoredOAuthConsent {
  consent: OAuthConsentData;
  savedAt: number; // Date.now() timestamp
}

const WIZARD_STORAGE_KEY = 'flowio_signup_wizard';
const OAUTH_CONSENT_KEY = 'flowio_oauth_consent';
const TTL_MS = 30 * 60 * 1000; // 30 minutes
const OAUTH_CONSENT_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Initial wizard state
 */
export const initialWizardState: WizardState = {
  currentStep: 1,
  termsReviewed: false,
  privacyReviewed: false,
  termsAccepted: false,
  gdprConsent: false,
  marketingConsent: false,
  consentTimestamp: null,
  selectedMethod: null,
  name: '',
  email: '',
  startedAt: new Date().toISOString(),
  lastActiveAt: new Date().toISOString(),
};

interface StoredWizardData {
  state: WizardState;
  savedAt: number;
}

/**
 * Save wizard state to sessionStorage
 *
 * @param state - Current wizard state
 */
export function saveWizardState(state: WizardState): void {
  try {
    const data: StoredWizardData = {
      state: {
        ...state,
        lastActiveAt: new Date().toISOString(),
      },
      savedAt: Date.now(),
    };
    sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage not available - graceful degradation
    // State will not persist across refreshes
  }
}

/**
 * Load wizard state from sessionStorage
 *
 * Returns null if:
 * - No stored state exists
 * - State has expired (>30 minutes)
 * - State is corrupted
 *
 * @returns Stored wizard state or null
 */
export function loadWizardState(): WizardState | null {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return null;

    const data: StoredWizardData = JSON.parse(raw);

    // Check TTL
    if (Date.now() - data.savedAt > TTL_MS) {
      clearWizardState();
      return null;
    }

    // Validate stored state has expected shape
    if (!data.state || typeof data.state.currentStep !== 'number') {
      clearWizardState();
      return null;
    }

    return data.state;
  } catch {
    // Corrupted or unavailable storage
    clearWizardState();
    return null;
  }
}

/**
 * Clear wizard state from sessionStorage
 */
export function clearWizardState(): void {
  try {
    sessionStorage.removeItem(WIZARD_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Save OAuth consent data before OAuth redirect
 *
 * This data survives the OAuth redirect and is read by the callback handler
 * to pass consent timestamps to the backend.
 * Data includes a timestamp for TTL validation (30 minutes).
 *
 * @param consent - Consent data to save
 */
export function saveOAuthConsent(consent: OAuthConsentData): void {
  try {
    const data: StoredOAuthConsent = {
      consent,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(OAUTH_CONSENT_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage not available - OAuth flow will fail gracefully
  }
}

/**
 * Load OAuth consent data (called after OAuth redirect)
 *
 * Returns null if:
 * - No stored consent exists
 * - Consent has expired (>30 minutes)
 * - Consent data is corrupted
 *
 * @returns Stored consent data or null
 */
export function loadOAuthConsent(): OAuthConsentData | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_CONSENT_KEY);
    if (!raw) return null;

    const data: StoredOAuthConsent = JSON.parse(raw);

    // Check TTL - consent expires after 30 minutes
    if (Date.now() - data.savedAt > OAUTH_CONSENT_TTL_MS) {
      clearOAuthConsent();
      return null;
    }

    const consent = data.consent;

    // Basic validation
    if (
      typeof consent.termsAccepted !== 'boolean' ||
      typeof consent.consentTimestamp !== 'string'
    ) {
      clearOAuthConsent();
      return null;
    }

    return consent;
  } catch {
    clearOAuthConsent();
    return null;
  }
}

/**
 * Clear OAuth consent data
 */
export function clearOAuthConsent(): void {
  try {
    sessionStorage.removeItem(OAUTH_CONSENT_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Check if sessionStorage is available
 * Useful for showing appropriate UX when storage is unavailable
 */
export function isStorageAvailable(): boolean {
  try {
    const test = '__flowio_storage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}
